import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ActiveFilters, FilterSection } from '../../../../layout/filter-sidebar/filter.model';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { X, LucideIconData } from 'lucide-angular';
import { formatCrc } from '../../../../shared/utils/currency.util';

export interface FilterChip {
  sectionId: string;
  value: string;
  label: string;
}

@Component({
  selector: 'app-active-filters-bar',
  imports: [IconComponent, ButtonComponent],
  templateUrl: './active-filters-bar.component.html',
  styleUrl: './active-filters-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class ActiveFiltersBarComponent {
  activeFilters = input.required<ActiveFilters>();
  sections = input.required<FilterSection[]>();

  removeFilter = output<{ sectionId: string; value: string }>();
  clearAll = output<void>();

  protected readonly closeIcon: LucideIconData = X;

  protected chips = computed<FilterChip[]>(() => {
    const filters = this.activeFilters();
    const secs = this.sections();
    const result: FilterChip[] = [];

    for (const [sectionId, value] of Object.entries(filters)) {
      if (!value) continue;
      const section = secs.find(s => s.id === sectionId);
      if (!section) continue;

      if (section.type === 'range' && typeof value === 'string' && value.includes('-')) {
        const [min, max] = value.split('-');
        if (min || max) {
          const label = `${section.title}: ${min ? formatCrc(Number(min)) : '...'} — ${max ? formatCrc(Number(max)) : '...'}`;
          result.push({ sectionId, value: value, label });
        }
      } else if (Array.isArray(value)) {
        for (const v of value) {
          const opt = section.options.find(o => o.value === v);
          result.push({ sectionId, value: v, label: opt?.label ?? v });
        }
      } else if (typeof value === 'string' && value !== 'all') {
        const opt = section.options.find(o => o.value === value);
        result.push({ sectionId, value, label: opt?.label ?? value });
      }
    }

    return result;
  });

  protected hasFilters = computed(() => this.chips().length > 0);
}
